import React, { useState } from 'react';
import { Button, Space, Switch } from 'antd';
import {
  Link,
  ErrorHandler,
  LoadingState,
  Drawer,
  Gutter,
  Gap,
} from '@common/components';
import { WorkforceGroupForm } from '@scheduling/components/admin-workforce-group-form/workforce-group-form';
import Text from 'antd/lib/typography/Text';
import { useMutation, useQuery } from '@common/hooks';
import { UpdateWorkforceGroupOrgBusinessConfig } from '@scheduling/graphql/mutations/workforce-group';
import { createMessage } from 'works/legacy/lib/message-helper';
import { convertToType } from '@scheduling/services/workforce-group-utils';
import { WorkforceGroupOrganizationBusinessConfigQueryCgResponse } from '@common/graphql/schema';
import { WORKFORCE_ORGANIZATION_BUSINESS_CONFIG } from '@institutions/graphql';
import { buildDialogUrl } from '@common/services';

export type WorkforceGroupOrgBusinessInputData = {
  name: string;
  gateOpenShiftsVisibility: boolean;
  openShiftsVisibilityDelayHoursNumber: string;
  openShiftsVisibilityDelayHoursType: string;
  gateEarliestAdvanceTime: boolean;
  earliestAdvanceTimeNumber: string;
  earliestAdvanceTimeType: string;
  useDefaultSettings?: boolean;
};

const inputDataDefault: WorkforceGroupOrgBusinessInputData = {
  name: '',
  gateOpenShiftsVisibility: false,
  gateEarliestAdvanceTime: false,
  earliestAdvanceTimeNumber: '0',
  earliestAdvanceTimeType: 'days',
  openShiftsVisibilityDelayHoursNumber: '0',
  openShiftsVisibilityDelayHoursType: 'days',
  useDefaultSettings: false,
};

type SelectType = 'days' | 'hours';

type BussConfigWithDefault =
  WorkforceGroupOrganizationBusinessConfigQueryCgResponse['workforceGroupOrganizationBusinessConfig'];

export function WorkforceGroupOrgBusinessUpdateDrawer({
  open,
  onClose,
  wfgConfigId,
}: {
  open: boolean;
  onClose: () => void;
  wfgConfigId?: string;
}) {
  const [inputData, setInputData] = useState<
    WorkforceGroupOrgBusinessInputData | null | undefined
  >(inputDataDefault);
  const [defaultWorkforceGroupConfig, setDefaultWorkforceGroupConfig] =
    useState<WorkforceGroupOrgBusinessInputData | null>(null);

  const [updateWfgOrgBussConfig, { loading: loadingUpdate }] = useMutation(
    UpdateWorkforceGroupOrgBusinessConfig,
    {
      onCompleted: (data) => {
        if (
          data?.updateWorkforceGroupOrgBusinessConfig?.errors &&
          data?.updateWorkforceGroupOrgBusinessConfig.errors.length > 0
        ) {
          createMessage(
            'error',
            `Failed to save the Group Settings. ${data?.updateWorkforceGroupOrgBusinessConfig?.errors?.[0]?.fullErrors[0]}.`
          );
          return;
        }
        createMessage('success', 'Group Settings successfully saved');
        setInputData(inputDataDefault);
        onClose();
      },
      onError: () => {
        createMessage(
          'error',
          "Group Settings couldn't be updated at this time. Try again later or contact support."
        );
      },
      refetchQueries: [
        'WorkforceGroupOrganizationBusinessConfig',
        'PaginatedWorkforceGroupOrgBusinessConfig',
      ],
    }
  );

  const buildDataForm = (
    data:
      | BussConfigWithDefault
      | BussConfigWithDefault['workforceGroup']['defaultConfig']
  ) => {
    const {
      gateOpenShiftsVisibility,
      gateEarliestAdvanceTime,
      earliestAdvanceTime,
      earliestAdvanceTimeFormat,
      openShiftsVisibilityDelayHours,
      openShiftsVisibilityDelayHoursFormat,
    } = data || {};

    return {
      gateOpenShiftsVisibility: gateOpenShiftsVisibility ?? false,
      gateEarliestAdvanceTime: gateEarliestAdvanceTime ?? false,
      earliestAdvanceTimeNumber: convertToType(
        earliestAdvanceTime?.toString() || '0',
        'hours',
        (earliestAdvanceTimeFormat?.toString() ||
          inputDataDefault.earliestAdvanceTimeType) as SelectType
      ).toString(),
      earliestAdvanceTimeType: earliestAdvanceTimeFormat || 'days',
      openShiftsVisibilityDelayHoursNumber: convertToType(
        openShiftsVisibilityDelayHours?.toString() || '0',
        'hours',
        (openShiftsVisibilityDelayHoursFormat?.toString() ||
          inputDataDefault.openShiftsVisibilityDelayHoursType) as SelectType
      ).toString(),
      openShiftsVisibilityDelayHoursType:
        openShiftsVisibilityDelayHoursFormat || 'days',
    };
  };

  const updateDefaultWorkforceGroupConfig = (
    response: BussConfigWithDefault | null
  ) => {
    if (!response?.workforceGroup?.defaultConfig) {
      return;
    }

    setDefaultWorkforceGroupConfig({
      name: response?.workforceGroup?.name,
      ...buildDataForm(response?.workforceGroup.defaultConfig),
    });
  };

  const updateInputData = (response: BussConfigWithDefault | null) => {
    if (!response?.workforceGroup) {
      return;
    }
    const { useDefaultSettings } = response;

    updateDefaultWorkforceGroupConfig(response);

    setInputData({
      name: response?.workforceGroup?.name,
      ...buildDataForm(response),
      useDefaultSettings,
    });
  };

  const { loading, data, error } = useQuery(
    WORKFORCE_ORGANIZATION_BUSINESS_CONFIG,
    {
      variables: {
        configId: wfgConfigId || '',
      },
      skip: !wfgConfigId,
      onCompleted: (response) => {
        updateInputData(response.workforceGroupOrganizationBusinessConfig);
      },
    }
  );
  const config = data?.workforceGroupOrganizationBusinessConfig;
  const workforceGroupId = config?.workforceGroup?.id;
  const businessName = config?.organizationBusiness?.name;

  const onUpdateInputData = (
    key: string,
    value: string | boolean | number | [number, number]
  ) => {
    setInputData((prev) => {
      if (!prev) return;

      return { ...prev, [key]: value };
    });
  };

  const onSave = () => {
    if (!inputData) return;
    const data = {
      configId: wfgConfigId || '',
      useDefaultSettings: inputData.useDefaultSettings ?? false,
      gateEarliestAdvanceTime: inputData.gateEarliestAdvanceTime,
      earliestAdvanceTime: convertToType(
        inputData.earliestAdvanceTimeNumber,
        inputData.earliestAdvanceTimeType as 'days' | 'hours',
        'hours'
      ),
      earliestAdvanceTimeFormat: inputData.earliestAdvanceTimeType,
      gateOpenShiftsVisibility: inputData.gateOpenShiftsVisibility,
      openShiftsVisibilityDelayHours: convertToType(
        inputData.openShiftsVisibilityDelayHoursNumber,
        inputData.openShiftsVisibilityDelayHoursType as 'days' | 'hours',
        'hours'
      ),
      openShiftsVisibilityDelayHoursFormat:
        inputData.openShiftsVisibilityDelayHoursType,
    };

    updateWfgOrgBussConfig({
      variables: {
        input: data,
      },
    });
  };

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="WorkforceGroupOrgBusinessUpdateDrawer"
        messageType="contextual"
      />
    );
  }

  const onCloseDrawer = () => {
    updateInputData(null);
    onClose();
  };

  const footer = (
    <Space>
      <Button size="large" onClick={onCloseDrawer}>
        Cancel
      </Button>
      <Button
        type="primary"
        size="large"
        onClick={() => onSave()}
        loading={loadingUpdate}
      >
        Save
      </Button>
    </Space>
  );

  const workforceGroupUpdateDrawerUrl = buildDialogUrl(
    'workforce-group-update-drawer',
    {
      props: {
        workforceGroupId: workforceGroupId || '',
      },
      overlap: true,
    }
  );

  return (
    <Drawer
      open={open}
      className="admin-wf-group-update-drawer"
      title={
        loading ? 'Edit Group' : `Edit ${inputData?.name} - ${businessName}`
      }
      footer={footer}
      onClose={onCloseDrawer}
    >
      {!inputData || loading ? (
        <LoadingState />
      ) : (
        <WorkforceGroupForm
          disableName
          disabled={inputData.useDefaultSettings}
          onUpdateInputData={onUpdateInputData}
          inputData={
            inputData.useDefaultSettings
              ? defaultWorkforceGroupConfig
              : inputData
          }
          shiftDistributionHeader={() => (
            <Gutter direction="vertical" size="sm">
              <Gap size="sm" align="center">
                <Switch
                  checked={inputData?.useDefaultSettings}
                  onChange={(e) => onUpdateInputData('useDefaultSettings', e)}
                />
                <Text>Use default settings</Text>
              </Gap>
            </Gutter>
          )}
          showBanner={!inputData?.useDefaultSettings}
          bannerContent={
            <Text>
              You are editing Group settings for this specific location. If
              you&apos;d like to update Group settings for all locations, go to{' '}
              <Link to={workforceGroupUpdateDrawerUrl}>Group Settings</Link>.
            </Text>
          }
        />
      )}
    </Drawer>
  );
}
